import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'react-hot-toast';
import { Plus, Trash2, Pencil, ShieldCheck } from 'lucide-react';
import { rbacApi } from '../../services/rbacApi';

const empty = { username: '', full_name: '', email: '', password: '', roles: [], enabled: true };

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...empty });

  const load = () => {
    rbacApi.listUsers().then((r) => setUsers(r.data)).catch(() => toast.error('Failed to load users'));
    rbacApi.listRoles().then((r) => setRoles(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, full_name: u.full_name, email: u.email || '', password: '', roles: u.roles || [], enabled: u.enabled });
    setOpen(true);
  };

  const toggleRole = (role) =>
    setForm((f) => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter((r) => r !== role) : [...f.roles, role] }));

  const save = async () => {
    try {
      if (editing) {
        const payload = { full_name: form.full_name, email: form.email, roles: form.roles, enabled: form.enabled };
        if (form.password) payload.password = form.password;
        await rbacApi.updateUser(editing.user_id, payload);
        toast.success('User updated');
      } else {
        await rbacApi.createUser(form);
        toast.success('User created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (u) => {
    try { await rbacApi.deleteUser(u.user_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{users.length} user(s)</p>
        <Button className="bg-orange-600 hover:bg-orange-700" onClick={openNew} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-1" />New User
        </Button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-2 text-left">Username</th>
              <th className="px-4 py-2 text-left">Full Name</th>
              <th className="px-4 py-2 text-left">Roles</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {users.map((u) => (
              <tr key={u.user_id} className="hover:bg-gray-50" data-testid={`user-row-${u.username}`}>
                <td className="px-4 py-2 font-medium flex items-center gap-1">
                  {u.is_superuser && <ShieldCheck className="w-3.5 h-3.5 text-orange-600" />}{u.username}
                </td>
                <td className="px-4 py-2">{u.full_name}</td>
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(u.roles || []).map((r) => (
                      <span key={r} className="text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full">{r}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {u.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="px-4 py-2 text-right space-x-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`}><Pencil className="w-4 h-4" /></Button>
                  {u.username !== 'admin' && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(u)} data-testid={`delete-user-${u.username}`}><Trash2 className="w-4 h-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit User' : 'New User'}</DialogTitle>
            <DialogDescription>Set the user's login details and assign roles for access control.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Username</Label>
              <Input value={form.username} disabled={!!editing} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username" />
            </div>
            <div>
              <Label>Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label>{editing ? 'New Password (leave blank to keep)' : 'Password'}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
            <div>
              <Label>Roles</Label>
              <div className="mt-1 max-h-40 overflow-y-auto border rounded p-2 grid grid-cols-2 gap-1">
                {roles.map((r) => (
                  <label key={r.role_id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.roles.includes(r.name)} onChange={() => toggleRole(r.name)} data-testid={`user-role-${r.name}`} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <Label>Enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={save} data-testid="save-user-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersTab;
